/* eslint-disable no-param-reassign */
import { createImmerReducer } from '@reducers/utils/createReducer';
import { RealTimeMachineData } from '_clean-code/types/realtime';
import { SortedMachineList } from '_clean-code/services/contracts/realtime.service';
import { StopItemType } from '_clean-code/services/contracts/stops.service';
import * as c from './types';
import { AppLocalStorageValues } from '_clean-code/utils/localStorageHelper';

type RealtimeMachineDataType = {
  [x: number]: RealTimeMachineData | null;
};

type ShiftStopsDataType = {
  [x: number]: StopItemType[] | null;
};

export type MachineItem = {
  id: number;
  name: string;
};

export type TvModeMachineItem = {
  id: number;
  name: string;
  mode: 'kpi' | 'multi';
};

export type MachineSortConfig = {
  ids: number[];
  isLoading: boolean;
  metricType: string;
  sortType: 'asc' | 'desc';
};

export enum EMachineListsVariants {
  Default = 'default',
  Shift = 'shift'
}

export interface RealTimeMachineState {
  machinesToDisplay: MachineItem[];
  machinesData?: RealtimeMachineDataType;
  shiftsStopsData?: ShiftStopsDataType;
  selectedShift?: string;
  loadingShifts: boolean;
  machineSortPreferences: Record<EMachineListsVariants, MachineSortConfig>;
}

const initialState: RealTimeMachineState = {
  machinesToDisplay: [],
  machinesData: {},
  selectedShift: undefined,
  loadingShifts: true,
  machineSortPreferences: {
    [EMachineListsVariants.Default]: {
      ids: [],
      isLoading: false,
      metricType: 'machine_title',
      sortType: 'asc'
    },
    [EMachineListsVariants.Shift]: {
      ids: [],
      isLoading: false,
      metricType: 'machine_title',
      sortType: 'asc'
    }
  }
};

export default createImmerReducer(initialState, {
  [c.FETCH_REALTIME_DATA_SUCCESS]: (
    draft: RealTimeMachineState,
    { payload }: { payload?: RealTimeMachineData[] }
  ) => {
    if (!payload) return;
    const machinesData: RealtimeMachineDataType = {};
    payload.forEach((x) => {
      machinesData[x.machineId ?? 0] = x;
    });
    draft.machinesData = {
      ...draft.machinesData,
      ...machinesData
    };
  },
  [c.ADD_MACHINES_TO_DISPLAY_LIST]: (
    draft: RealTimeMachineState,
    { payload }: { payload?: MachineItem[] }
  ) => {
    if (!payload) return;
    draft.machinesToDisplay = [...draft.machinesToDisplay, ...payload];
  },
  [c.REMOVE_MACHINES_FROM_DISPLAY_LIST]: (
    draft: RealTimeMachineState,
    { payload }: { payload?: number[] }
  ) => {
    if (!payload) return;
    draft.machinesToDisplay = draft.machinesToDisplay.filter(
      (x) => !payload.includes(x.id)
    );
  },
  [c.RESET_MACHINES_TO_DISPLAY_LIST]: (draft: RealTimeMachineState) => {
    draft.machinesToDisplay = [];
  },
  [c.FETCH_STOP_DATA_BY_SHIFT_SUCCESS]: (
    draft: RealTimeMachineState,
    { payload }: { payload?: { stops: MachineItem[]; shiftId: number } }
  ) => {
    if (!payload) return;
    draft.shiftsStopsData = {
      ...draft.shiftsStopsData,
      [payload.shiftId]: payload.stops
    };
  },
  [c.EMPTY_MACHINE_DATA]: (draft: RealTimeMachineState) => {
    draft.machinesData = {};
    draft.shiftsStopsData = {};
  },
  [c.SET_SELECTED_SHIFT]: (
    draft: RealTimeMachineState,
    { payload }: { payload?: string }
  ) => {
    draft.selectedShift = payload;
  },
  [c.FETCH_SORTED_MACHINE_LIST]: (
    draft: RealTimeMachineState,
    {
      payload
    }: {
      payload?: {
        variant: EMachineListsVariants;
        sortType: 'asc' | 'desc';
        metricType: string;
      };
    }
  ) => {
    if (!payload) return;
    const variant = payload.variant ?? EMachineListsVariants.Default;
    draft.machineSortPreferences[variant].isLoading = true;
    draft.machineSortPreferences[variant].ids = [];
    draft.machineSortPreferences[variant].metricType = payload.metricType;
    draft.machineSortPreferences[variant].sortType = payload.sortType;
  },
  [c.FETCH_SORTED_MACHINE_LIST_SUCCESS]: (
    draft: RealTimeMachineState,
    {
      payload
    }: {
      payload?: SortedMachineList & {
        variant: EMachineListsVariants;
        sortType: 'asc' | 'desc';
        metricType: string;
      };
    }
  ) => {
    if (!payload) return;
    const variant = payload.variant ?? EMachineListsVariants.Default;
    draft.machineSortPreferences[variant].isLoading = false;
    draft.machineSortPreferences[variant].ids = payload.machinesIds;
    draft.machineSortPreferences[variant].metricType = payload.metricType;
    draft.machineSortPreferences[variant].sortType = payload.sortType;
  },
  [c.LOAD_MACHINES_SORTING_FILTER]: (
    draft: RealTimeMachineState,
    {
      payload
    }: { payload?: { companyId: number; variant: EMachineListsVariants } }
  ) => {
    if (!payload) return;
    const appLocalStorage = new AppLocalStorageValues(+payload.companyId);
    if (payload.variant === EMachineListsVariants.Default) {
      const realtimeFilters = appLocalStorage.realTimeFilters;
      draft.machineSortPreferences[EMachineListsVariants.Default].metricType =
        realtimeFilters.selectedMetric ?? 'machine_title';
      draft.machineSortPreferences[EMachineListsVariants.Default].sortType =
        realtimeFilters.sortType ?? 'asc';
    } else {
      const machinesShiftSortPreferences =
        appLocalStorage.machinesShiftSortPreferences;
      draft.machineSortPreferences[EMachineListsVariants.Shift].metricType =
        machinesShiftSortPreferences.metricType ?? 'machine_title';
      draft.machineSortPreferences[EMachineListsVariants.Shift].sortType =
        machinesShiftSortPreferences.sortType ?? 'asc';
    }
  }
});
